import dynamicImportLoad from "@helpers/dynamic-import-load.js";
import { isObject, toArray } from "@helpers/utils.js";
import model from "@js/model.js";
import Enum from "@shared/enum.js";
import { watch } from "vue";
import controllerSingleton from "../controller.js";
import { NoMessagesLabel } from "@lang/inbox.js";
import { AcceptLabel, AcceptLabelFreeBet, AcceptLabelFreeSpins } from "@lang/misc.js";
import { PlayNowLabel } from "@lang/user.js";
import { DepositNowLabel } from "@lang/cashier.js";
import { LearnMoreLabel } from "@lang/promo.js";

const POLL_INTERVAL = 30000; //30s
// const POLL_INTERVAL = 100; //30s
let pollTimeout = null;
let pollingPaused = false;

export default {
    initOptimove(){
        model.observables.inboxUnreadMsgs = 0;

        model.pushLang({NoMessagesLabel, AcceptLabel, PlayNowLabel, DepositNowLabel, LearnMoreLabel, AcceptLabelFreeSpins, AcceptLabelFreeBet});

        if(window.optimoveSDK?.API){
            model.dispatchEvent('OptimoveSDKLoaded');
            optimoveSDK.API.setRealTimeOptions({
                reportEventCallback: reportEventCallback.bind(this),
            });

            model.on('LoginComplete', () => {
                console.log('OPTIMOVE LOGIN USER: ' + model.userId);
                optimoveSDK.API.setUserId(model.userId);
            });

            // model.on('LogoutComplete', () => {
            //     console.log('OPTIMOVE LOGOUT USER');
            //     optimoveSDK.API.signOutUser();
            // });
        }

        async function reportEventCallback(resp){
            if(!isObject(resp) || !resp.IsSuccess){
                console.warn('Optimove event reporting failed', resp);
                return;
            }

            let data = resp.Data;
            if(!data || typeof data !== 'string') return;

            let emptyContainer = data.includes('data-empty-container="true"');

            const popupCss = import.meta.glob('@css/optimove-popup.scss', {query: {inline: ''}});
            const popupCssPromise = popupCss[Object.keys(popupCss || {})[0]];
            let css = (await dynamicImportLoad(popupCssPromise))?.default;

            if(css){
                data += `<style>${css}</style>`;
                data = /*html*/`<div class="optimove-popup" data-site-key="${model.siteKey}">${data}</div>`
            }

            this.showNotification({
                title: null,
                text: data,
                shadowRoot: true,
                emptyContainer,
                customClass: 'optimove',
                noBtns: true,
                overlayClose: false,
            });
        }

        if(!isObject(model.optimoveConfig)) return;

        if(model.inboxActive){
            let dispose = null;
            model.on('LoginComplete', () => {
                setTimeout(() => pollInbox.call(this), 500);

                dispose = watch(() => model.observables.inboxData, value => {
                    if(!value) return;
                    dispose?.();
                    dispose = null;

                    let firstUnread = model.observables.inboxData?.find(msg => !msg.readAt);
                    if(!firstUnread) return;

                    if((firstUnread.payload?.DELIVERY_MODE + '').toLowerCase() != 'popup') return;

                    firstUnread.open = true;
                    controllerSingleton.goTo(Enum.UIContexts.INBOX);
                });
            });

            let lastHideState = 0;
            document.addEventListener('visibilitychange', () => {
                if(!model.logged) return;

                if (document.visibilityState === 'visible') {
                    let mills = POLL_INTERVAL - (Date.now() - lastHideState);
                    if(mills < 0) mills = 0;
                    this.resumePolling(mills);
                }
                else{
                    clearTimeout(pollTimeout);
                    lastHideState = Date.now();
                }
            });

            model.on('LogoutComplete', () => {
                dispose?.();
                dispose = null;
                model.observables.inboxUnreadMsgs = 0;
                model.observables.inboxData = null;
                clearTimeout(pollTimeout);
            });

            model.on('InboxMessageRead', () => {
                this.resumePolling();
            });
        }
    },
    reportMessageRead(msg){
        if(!window.optimoveSDK?.API) return;
        return optimoveSDK.API.embeddedMessaging.setReadAsync(msg, true);
    },
    deleteMessage(msg){
        if(!window.optimoveSDK?.API) return Promise.resolve();

        return new Promise(resolve => {
            let retires = 0;

            this.pausePolling();
            inner.call(this);
            function inner(){
                //43f4c569-8140-40ba-b6bc-31fc0b4d1929
                fetch(`https://optimobile-inbox-srv-eu.optimove.net/api/v1/messages/${msg.id}?TenantId=1754&BrandId=${model.optimoveConfig.apiBrandId}`, {method: 'DELETE'})
                // .then(res => res.json())
                .then(res => {
                    console.log(res);
                    onResolve.call(this);
                })
                .catch(err => {
                    console.error(err);
                    setTimeout(() => {
                        retires++;
                        if(retires < 5) inner.call(this);
                        else onResolve.call(this);
                    }, 500);
                });
            }

            function onResolve(){
                resolve();
                this.resumePolling(2000);
            }
        });
    },
    sendLobbyCommand(lobbyCommand, strValues){
        let params = deconstructBtnValues.call(this, strValues);

        let options = {
            brand: model.isUI('FUND') ? 'Fundalor' : 'Skythor',
            command: lobbyCommand,
        };

        Object.assign(options, params);

        if(!window.optimoveSDK?.API) {
            console.log('LOBBY COMMAND:', options);
            return;
        }

        window.optimoveSDK.API.reportEvent('lobby_command', options);
    },
    resumePolling(mills){
        pollingPaused = false;
        clearTimeout(pollTimeout);
        if(!model.logged || document.visibilityState !== 'visible') return;
        pollTimeout = setTimeout(() => pollInbox.call(this), typeof mills === 'undefined' ? POLL_INTERVAL : mills);
    },
    pausePolling(){
        pollingPaused = true;
        clearTimeout(pollTimeout);
    },
    activateFreeGamesPromoAndStartGame(value, callback){
        if(!value) return;

        this.sendUserPromotionActivationRequest(value, data => {
            let response = toArray(data.messages?.message).find(x => x.userPromotionActivationMessage)?.userPromotionActivationMessage?.response;
            if(!response?.success) return;

            response.id = response.promotionId;
            
            callback?.(response);

            if(response.wheel_segments){
                model.dispatchEvent('WOFFromInbox', {
                    wheelPromo: response,
                });
                return;
            }

            if(response.gameId == 97) {
                this.gotoCategory({level1: 10002});
                return;
            }

            if(response.gameId) this.startGame(response.gameId);
        });
    },
}

function deconstructBtnValues(strValues){
    let params = {};
    if(typeof strValues === 'string') {
        strValues = strValues.split('&');
        strValues.forEach(pair => {
            pair = pair.replace(/\s+/g, '').split('=');
            params[pair[0]] = pair[1];
        });
    }
    return params;
}

function pollInbox(){
    if(!model.logged || document.visibilityState !== 'visible' || pollingPaused) return;

    clearTimeout(pollTimeout);
    getMessagesAsync().then(data => {
        if(pollingPaused) return;

        let newInboxData = data?.Inbox?.messages;
        if(!Array.isArray(newInboxData)) return;

        model.observables.inboxUnreadMsgs = newInboxData.filter(msg => !msg.readAt).length;

        newInboxData.forEach((msg, index) => {
            let oldMsg = model.observables.inboxData?.find(m => m.id == msg.id);
            if(oldMsg){
                if(oldMsg.open) msg.open = true;
            }

            // debugger;

            let payload = msg.payload;
            if(isObject(payload) && Object.keys(payload).length > 0){
                let bg = payload.BACKGROUND;
                let mobileBg = payload.BACKGROUND_MOBILE;
                
                if(bg) msg.bg = bg;
                if(mobileBg) msg.mobileBg = mobileBg;
                
                if(isObject(model.optimoveConfig.msgButtons)){
                    msg.buttons = [];
                    for(let key in payload){
                        let val = payload[key];
    
                        if(!Object.keys(model.optimoveConfig.msgButtons).includes(key)) continue;
                        
                        if(key == 'SHOW_MORE') msg.url = null;
    
                        let btn = model.optimoveConfig.msgButtons[key];
                        let text = btn.label;
                        if(isObject(text)) text = model.redeemLangObj(text);
                        else text = model.getStr(text);
    
                        msg.buttons.push({ 
                            type: btn.type,
                            command: btn.command, 
                            val,
                            text, 
                        });
                    }
                }
            }
        });

        model.observables.inboxData = newInboxData;

        this.resumePolling();
    });
}

function getMessagesAsync(){
    if(!window.optimoveSDK?.API) {
        return new Promise(async resolve => {
            const resp = await fetch(`http://localhost:80/optimove-embedded-messages-test/`);
            resolve(await resp.json());
        });
    }

    return optimoveSDK.API.embeddedMessaging.getMessagesAsync();
}